import { isEqual } from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';

import 'react-dates/initialize'; // Needs to be imported before date picker components
import { DateRangePicker, isInclusivelyBeforeDay } from 'react-dates';

import analyticService from '../../../services/analytics';
import { dateService } from '../../../services/date';

import { getClass } from '../../../utils/component-helpers';
import Presets from './presets';

import 'react-dates/lib/css/_datepicker.css';
import './date-picker.scss';

class DatePicker extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;

    this.prevStart = this.props.dateRange.startDate;
    this.prevEnd = this.props.dateRange.endDate;
    // Used on date changes to ensure order (startDate, then endDate)
    this.dateCycle = 'start';
    // Used to distinguish between clicked dates and typed dates
    this.inputChange = false;

    this.datePicker = null;
    this.inputs = null;
    this.presetRanges = dateService.getPresetDateRanges();
    this.dateFormats = {
      short: 'MMM D',
      long: 'MMM D, YYYY',
      full: 'MMM DD, YYYY',
    };

    this.state = {
      // Date range that the user clicks even if they don't plan to apply it
      startDate: this.props.dateRange.startDate,
      endDate: this.props.dateRange.endDate,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClick.bind(this));

    this.inputs = this.datePicker.querySelectorAll('.DateInput.DateInput_1 input');
    this.inputs.forEach(input => {
      input.addEventListener('keydown', this.keydownListener.bind(this));
      input.addEventListener('click', this.onDateInputClick.bind(this));
    });
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClick.bind(this));

    this.inputs.forEach(input => {
      input.removeEventListener('keydown', this.keydownListener.bind(this));
      input.removeEventListener('click', this.onDateInputClick.bind(this));
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.focusedInput && this.state.focusedInput) {
      this.AnalyticService.trackEvent('Mega Tables', 'Click', `${this.props.eventPrefix}`);
    }

    // Handle preset selected state based off of date range
    if (!isEqual(prevProps.dateRange, this.props.dateRange)) {
      this.setState(this.props.dateRange, () => {
        this.resetPreviousDates();
        this.checkForPresets();
      });
    }

    const startDate = document.getElementById('start-date');
    if (startDate) this.formatDateRangeDisplay(startDate, this.state.focusedInput);
  }

  keydownListener(event) {
    event.stopPropagation();
    this.inputChange = true;
  }

  handleClick(event) {
    const eventTarget = event.target;
    if (!this.datePicker || this.datePicker.contains(eventTarget)) return;

    // Reset dates if clicking outside dropdown
    this.setState(this.props.dateRange, () => {
      this.resetPreviousDates();
    });
  }

  checkForPresets() {
    let selected = false;
    Object.keys(this.presetRanges).forEach(key => {
      const preset = this.presetRanges[key];
      if (
        this.compareDates(this.state.startDate, preset.startDate) &&
        this.compareDates(this.state.endDate, preset.endDate) &&
        !selected
      ) {
        preset.selected = true;
        /*
         * If a preset is already selected, we can ignore the rest to avoid
         * double highlighting in case the date range fits two presets
         */
        selected = true;
      } else {
        preset.selected = false;
      }
    });
  }

  formatDateRangeDisplay(startDate, datePickerOpened = false) {
    const endDate = document.getElementById('end-date');
    let formatType = 'short';

    if (this.sameYear() && !datePickerOpened) {
      startDate.value = dateService.formatDate(this.state.startDate, this.dateFormats[formatType]);
      endDate.value = dateService.formatDate(this.state.endDate, this.dateFormats.long);
      this.assignWidthAndMargin(startDate, formatType);
    } else if (this.sameYear() && datePickerOpened) {
      // Need full date format to modify date with keyboard inputs
      formatType = 'full';
      startDate.value = dateService.formatDate(this.state.startDate, this.dateFormats[formatType]);
      this.assignWidthAndMargin(startDate, formatType);
    } else if (!this.sameYear() && !datePickerOpened) {
      formatType = 'long';
      this.assignWidthAndMargin(startDate, formatType);
    }
  }

  presetClicked(preset) {
    const presetLabel = this.presetRanges[preset].label;
    this.AnalyticService.trackEvent(
      'Mega Tables',
      'Click',
      `${this.props.eventPrefix} ${presetLabel}`,
    );
    this.presetRanges[preset].selected = true;

    this.setState({
      startDate: this.presetRanges[preset].startDate,
      endDate: this.presetRanges[preset].endDate,
    });

    Object.keys(this.presetRanges).forEach(key => {
      if (key === preset) return;
      this.presetRanges[key].selected = false;
    });

    this.dateCycle = 'start';
    this.prevStart = this.presetRanges[preset].startDate;
    this.prevEnd = this.presetRanges[preset].endDate;
  }

  applyDateRange() {
    this.setState(
      {
        focusedInput: null, // Close date picker
      },
      () => {
        const [startDate, endDate] = this.getDateRanges();
        // Set date on parent component
        const formattedStartDate = dateService.formatDate(startDate);
        const formattedEndDate = dateService.formatDate(endDate);
        this.props.setDateRange(formattedStartDate, formattedEndDate);

        const gaDateFormat = 'MM/DD/YYYY';
        const gaStartDate = dateService.formatDate(startDate, gaDateFormat);
        const gaEndDate = dateService.formatDate(endDate, gaDateFormat);
        const gaLabel = `${this.props.eventPrefix} ${gaStartDate} - ${gaEndDate}`;
        this.AnalyticService.trackEvent('Mega Tables', 'Execute', gaLabel);
      },
    );
  }

  getDateRanges() {
    // If no end date is selected, default the end date to previously selected date
    let { startDate } = this.state;
    let { endDate } = this.state;
    if (!endDate) {
      endDate = this.props.dateRange.endDate;
      // If the start date is greater than the end date, default end date to today
      if (startDate > endDate) endDate = dateService.getCurrentDateTime();
      this.setState({ endDate });
    }
    if (!startDate) {
      startDate = this.props.dateRange.startDate;
      this.setState({ startDate });
    }
    return [startDate, endDate];
  }

  cancelDateRange() {
    // Set date back to original range and close date picker
    this.setState(Object.assign(this.props.dateRange, { focusedInput: null }));
    this.AnalyticService.trackEvent('Mega Tables', 'Click', `${this.props.eventPrefix} Cancel`);
  }

  /* Helper functions */
  sameYear() {
    const startDate = document.getElementById('start-date');
    return (
      startDate &&
      this.state.startDate &&
      this.state.endDate &&
      this.state.startDate.year() === this.state.endDate.year()
    );
  }

  compareDates(firstDate, secondDate) {
    return dateService.formatDate(firstDate) === dateService.formatDate(secondDate);
  }

  resetPreviousDates() {
    this.prevStart = this.props.dateRange.startDate;
    this.prevEnd = this.props.dateRange.endDate;
  }

  assignWidthAndMargin(element, type = 'full') {
    let width = '90px';
    let marginRight = '0';
    if (type === 'short') {
      width = '45px';
      marginRight = '10px';
    }

    element.style['max-width'] = width;
    element.style['margin-right'] = marginRight;
  }

  onDateChange(startDate, endDate) {
    // If user changes date via input fields, revert to normal DatePicker behavior
    if (this.inputChange) {
      return this.setState({ startDate, endDate }, () => {
        this.inputChange = false;
        this.prevStart = startDate;
        this.prevEnd = endDate;
      });
    }

    /*
     * Determine which of these dates has changed since last update.
     * Then, based on dateCycle, either set the startDate or endDate.
     */
    const newStartDate = !this.compareDates(startDate, this.prevStart);
    const newEndDate = !this.compareDates(endDate, this.prevEnd);

    if (this.dateCycle === 'start') {
      this.setState(
        {
          startDate: newStartDate ? startDate : endDate,
          endDate: null,
        },
        () => {
          this.dateCycle = 'end';
        },
      );
    } else {
      // Avoid the endDate if it resets to null
      const dateChange = newEndDate && endDate ? endDate : startDate;

      // Prevent endDate being earlier than startDate
      if (dateChange.isBefore(this.state.startDate)) {
        this.setState({ startDate: dateChange });
      } else {
        // When neither date changes, default to endDate
        const newDate = !newEndDate && !newStartDate ? endDate : newEndDate ? endDate : startDate;
        this.setState({ endDate: newDate }, () => {
          this.dateCycle = 'start';
        });
      }
    }

    this.prevStart = startDate;
    this.prevEnd = endDate;
  }

  onArrowClick(direction) {
    const gaLabel = `${this.props.eventPrefix} ${direction} Arrow`;
    this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
  }

  onDateInputClick(event) {
    const fieldName = event.target === this.inputs[0] ? 'Start Date' : 'End Date';
    const gaLabel = `${this.props.eventPrefix} ${fieldName} Field`;
    this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
  }

  render() {
    const presets = (
      <Presets
        apply={this.applyDateRange.bind(this)}
        cancel={this.cancelDateRange.bind(this)}
        presetClicked={this.presetClicked.bind(this)}
        presetRanges={this.presetRanges}
        disablePastDates={this.props.disablePastDates}
        allowFutureDates={this.props.allowFutureDates}
        futureLimit={this.props.futureLimit}
        pastDateThreshold={this.props.pastDateThreshold}
      ></Presets>
    );

    return (
      <div
        ref={node => (this.datePicker = node)}
        className={getClass('date-range-picker', {
          'calendar-open': this.state.focusedInput,
        })}
      >
        <DateRangePicker
          /* Calendar options */
          anchorDirection="right" // Align popup to the right of calendar
          calendarInfoPosition="after"
          customArrowIcon="-"
          displayFormat={this.dateFormats.full}
          endDateId="end-date" // Required
          startDateId="start-date" // Required
          hideKeyboardShortcutsPanel={true} // Hide shortcuts button
          keepOpenOnDateSelect={true}
          minimumNights={0} // Allows selecting the same start and end date
          noBorder={true} // Disables border on input date range box
          endDate={this.state.endDate}
          focusedInput={this.state.focusedInput}
          // Disable future dates
          isOutsideRange={day => {
            return (
              (!this.props.allowFutureDates &&
                !isInclusivelyBeforeDay(day, this.props.futureLimit)) ||
              (this.props.disablePastDates && !day.isAfter(this.props.pastDateThreshold))
            );
          }}
          onDatesChange={({ startDate, endDate }) => this.onDateChange(startDate, endDate)}
          onFocusChange={focusedInput => this.setState({ focusedInput })}
          onPrevMonthClick={() => this.onArrowClick('Left')}
          onNextMonthClick={() => this.onArrowClick('Right')}
          renderCalendarInfo={() => presets} // Render presets
          startDate={this.state.startDate}
        />
        <span
          className={this.state.focusedInput ? 'arrow-up' : 'arrow-down'}
          onClick={() => this.setState({ focusedInput: 'startDate' })}
        ></span>
      </div>
    );
  }
}

export default DatePicker;
